import PortalConnection from 'models/PortalConnection';
import Workflow from 'models/Workflow';
import { ActionStatus } from 'models/WorkflowAction';

import { ACTION_KINDS } from './constants';
import { taskCardBody, TaskReason, taskReason } from './taskCardCopy';

// Two kinds, and only two. The heterogeneous free-text card the design shows alongside these
// belongs to a far-future email->event feature; it is deliberately NOT modeled here, because a
// plugin registry for a tenant that does not exist buys nothing and costs a layer.

export interface PatientTaskCard {
  id: string;
  kind: 'patient';
  workflowId: string;
  // The patient, through Workflow#patientName's portal-aware chain.
  title: string;
  // What the portal itself calls this patient — the HUD's search param contract. Empty when
  // the portal never stated one; never the Olio-composed title (which can be an extracted
  // name or the 'New referral' sentinel).
  portalPatientName: string;
  // The source system, as the portal names itself.
  badge: string;
  sourceSystem: string;
  body: string;
  reason: TaskReason;
  createdAt: Date | null;
  retrievalMode: string;
  // What `Open in <portal>` needs. Absent when the run holds no usable portal pointer or no
  // retrieval action to address — the affordance then simply doesn't render.
  launch?: { actionId: string; entryUrl: string };
}

// Two operational cards ship. The captcha card the design draws is cut: the server cannot
// distinguish a sweeping, a halted and a never-started scan, so there is nothing to derive it
// from. Both of these route to Manage Connected Portals.
export type OperationalReason = 'credentialsRejected' | 'setupSignIn';

export interface OperationalTaskCard {
  id: string;
  kind: 'operational';
  reason: OperationalReason;
  sourceSystem: string;
  title: string;
  body: string;
  createdAt: null;
}

export type TaskCard = PatientTaskCard | OperationalTaskCard;

const URL_POINTER = /^https?:\/\//;

export interface PatientCardContext {
  // usePortalName's resolver — the server owns portal names, this module never guesses one.
  portalName: (sourceSystem?: string | null) => string;
}

export function patientTaskCard(workflow: Workflow, { portalName }: PatientCardContext): PatientTaskCard {
  const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
  const name = portalName(sourceSystem);
  const pointer = workflow.valueFor('retrieval_pointer')?.value ?? '';
  // Only a retrieval that is actually waiting on a person: a completed one would render a
  // pointless launch button (and on a re-run chain could hand the extension a retired
  // instance's actionId). Same presentation-reads-effectiveStatus rule as taskCardCopy.
  const retrieval = workflow.actions.find(
    (action) => action.kind === ACTION_KINDS.retrieve && action.effectiveStatus === ActionStatus.ACTION_REQUIRED
  );

  return {
    id: `task:${workflow.id}`,
    kind: 'patient',
    workflowId: workflow.id,
    title: workflow.patientName,
    portalPatientName: workflow.portalPatientName,
    badge: name,
    sourceSystem,
    body: taskCardBody(workflow, { portalName: name }),
    reason: taskReason(workflow),
    createdAt: workflow.createdAt,
    retrievalMode: workflow.retrievalMode ?? 'manual',
    launch: retrieval && URL_POINTER.test(pointer) ? { actionId: retrieval.id, entryUrl: pointer } : undefined,
  };
}

/**
 * The portal-level cards, derived from the Connected Portals rows — never from a portal check
 * hardcoded here. Readiness is carried by the row (`ready` / `blockers`), so this reads the
 * server's verdict rather than recomputing one that could disagree with the dispatch gate.
 */
export function operationalTaskCards(connections: PortalConnection[]): OperationalTaskCard[] {
  return connections.flatMap<OperationalTaskCard>((connection) => {
    const name = connection.displayName;

    // Lockout protection: this card offers NO retry, by design. Retrying a rejected password is
    // how an account gets locked, so the only way out is new credentials.
    if (connection.credentials?.status === 'invalid' || connection.blockers.includes('credentials_invalid')) {
      return [
        {
          id: `portal:${connection.sourceSystem}:credentialsRejected`,
          kind: 'operational' as const,
          reason: 'credentialsRejected' as const,
          sourceSystem: connection.sourceSystem,
          title: `${name} rejected your saved password`,
          body: `Olio will skip automatic scans of ${name} until your password is updated.`,
          createdAt: null,
        },
      ];
    }

    // Manual mode is a configuration, not a gap — a client that has chosen to do this by hand
    // must not be nagged to set up automation it didn't ask for.
    const setupBlockers = connection.blockers.filter((blocker) => blocker !== 'manual_mode');
    if (connection.isManual || connection.ready || setupBlockers.length === 0) return [];

    const needsCredentials = setupBlockers.includes('no_credentials');
    return [
      {
        id: `portal:${connection.sourceSystem}:setupSignIn`,
        kind: 'operational' as const,
        reason: 'setupSignIn' as const,
        sourceSystem: connection.sourceSystem,
        title: 'Setup automatic sign-in',
        body: needsCredentials
          ? `Save your ${name} sign-in so Olio can scan the portal for you.`
          : `Record how Olio should work ${name} so it can scan the portal for you.`,
        createdAt: null,
      },
    ];
  });
}

export type TaskSort = 'newest' | 'oldest';

export interface TaskFilters {
  sourceSystems: string[];
  reasons: TaskReason[];
}

export const NO_TASK_FILTERS: TaskFilters = { sourceSystems: [], reasons: [] };

export function filterPatientCards(cards: PatientTaskCard[], filters: TaskFilters): PatientTaskCard[] {
  return cards.filter(
    (card) =>
      (filters.sourceSystems.length === 0 || filters.sourceSystems.includes(card.sourceSystem)) &&
      (filters.reasons.length === 0 || filters.reasons.includes(card.reason))
  );
}

/**
 * Operational cards pin above patient cards. The pin is a property of the CARD KIND — an
 * operational card is a standing "this portal can't work until you fix it", which outranks any
 * one patient's task by construction — so no portal check appears here.
 *
 * Patient cards sort by the toolbar control, with an id tiebreak: the list refetches live, and
 * two runs created in the same second must not swap places under a reader's eyes.
 */
export function orderTaskCards(operational: OperationalTaskCard[], patient: PatientTaskCard[], sort: TaskSort) {
  const direction = sort === 'newest' ? -1 : 1;
  const sorted = [...patient].sort(
    (a, b) =>
      direction * ((a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0)) ||
      a.workflowId.localeCompare(b.workflowId)
  );
  const pinned = [...operational].sort((a, b) => a.id.localeCompare(b.id));
  return [...pinned, ...sorted] as TaskCard[];
}
